import { CommonModule } from '@angular/common';
import { Component, ElementRef, NgZone, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { VW_ERROR_CODES } from 'vwappservices';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { MachineTypesApiService } from '../../services/machine-types-api.service';
import { notifyResult, reportApiError } from '../../shared/api-result.helper';
import { AppendToBodyDirective } from '../../shared/append-to-body.directive';
import { ConfirmService } from '../../shared/confirm.service';
import { BASE_VIO_TABLE_V2_CONFIG } from '../../shared/vio-table-v2.config';
import { openModalById } from '../../shared/bootstrap-modal.helper';

interface DowntimeReason {
  _id: string;
  name: string;
  active: boolean;
}

interface MachineType {
  _id: string;
  name: string;
  description: string;
  active: boolean;
  downtimeReasons: DowntimeReason[];
}

@Component({
  selector: 'app-machine-types',
  standalone: true,
  imports: [CommonModule, FormsModule, AppendToBodyDirective, VioTablesV2Module],
  templateUrl: './machine-types.component.html',
  styleUrl: './machine-types.component.scss',
})
export class MachineTypesComponent implements OnInit {
  machineTypes: MachineType[] = [];
  tableData: any[] = [];
  loading = true;

  tableConfig: VT2_TABLE_CONFIG = {
    ...BASE_VIO_TABLE_V2_CONFIG,
    tableTitle: 'Machine Types',
    excelFileName: 'machine-types',
    excludeFields: ['_id'],
    columnLabels: { name: 'Machine Type', description: 'Description', downtimeReasonsCount: 'Downtime Reasons' },
    actionButtons: [
      {
        icon: '✏️',
        label: 'Edit',
        title: 'Edit machine type',
        cssClass: 'btn btn-outline-primary btn-sm vio-table-icon-btn vio-icon-edit',
        onClick: (row: any) => {
          const machineType = this.machineTypes.find((m) => m._id === row._id);
          if (!machineType) return;
          this.openEditModal(machineType);
          openModalById('machineTypeModal');
        },
      },
    ],
  };

  operation: 'Add' | 'Edit' = 'Add';
  editingId: string | null = null;
  machineTypeForm = { name: '', description: '' };
  newReasonName = '';
  // Staged downtime reason names for the Add flow — there's no machine type _id to attach them
  // to yet, so they're held here and sent along with the initial create() call instead.
  newMachineTypeReasons: string[] = [];

  @ViewChild('machineTypeModalCloseBtn') machineTypeModalCloseBtn!: ElementRef<HTMLButtonElement>;

  private toastr = inject(ToastrService);
  private confirmService = inject(ConfirmService);

  constructor(
    private machineTypesApi: MachineTypesApiService,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.load();
  }

  get editingMachineType(): MachineType | null {
    return this.editingId ? this.machineTypes.find((m) => m._id === this.editingId) || null : null;
  }

  async load() {
    this.loading = true;
    const res = await this.machineTypesApi.list();
    // Same zone-continuation issue documented in DashboardComponent/SettingsComponent's load() —
    // without this, `loading` flips to false internally but the view stays stuck.
    this.ngZone.run(() => {
      if (res.errorCode === VW_ERROR_CODES.SUCCESS) {
        this.machineTypes = res.Data;
        this.tableData = this.machineTypes.map((m) => ({
          _id: m._id,
          name: m.name,
          description: m.description || '—',
          downtimeReasonsCount: m.downtimeReasons.length,
        }));
      } else {
        reportApiError(this.toastr, res);
      }
      this.loading = false;
    });
  }

  openAddModal() {
    this.operation = 'Add';
    this.editingId = null;
    this.machineTypeForm = { name: '', description: '' };
    this.newReasonName = '';
    this.newMachineTypeReasons = [];
  }

  openEditModal(machineType: MachineType) {
    this.operation = 'Edit';
    this.editingId = machineType._id;
    this.machineTypeForm = {
      name: machineType.name,
      description: machineType.description,
    };
    this.newReasonName = '';
  }

  async saveMachineType() {
    if (!this.machineTypeForm.name) return;
    const res =
      this.operation === 'Add'
        ? await this.machineTypesApi.create({
            name: this.machineTypeForm.name,
            description: this.machineTypeForm.description,
            downtimeReasons: this.newMachineTypeReasons,
          })
        : await this.machineTypesApi.update(this.editingId!, { ...this.machineTypeForm });
    if (!notifyResult(this.toastr, res, `${this.machineTypeForm.name} ${this.operation === 'Add' ? 'added' : 'updated'}.`)) return;
    this.machineTypeModalCloseBtn.nativeElement.click();
    await this.load();
  }

  async addReason() {
    if (!this.editingId || !this.newReasonName) return;
    const res = await this.machineTypesApi.addReason(this.editingId, { name: this.newReasonName });
    if (!notifyResult(this.toastr, res, `Reason "${this.newReasonName}" added.`)) return;
    this.newReasonName = '';
    await this.load();
  }

  // Add-flow reasons are just staged locally (nothing to delete server-side yet), so no name is
  // reserved and no confirmation is needed the way deleteReason() needs one for a saved reason.
  addStagedReason() {
    const name = this.newReasonName.trim();
    if (!name) return;
    this.newMachineTypeReasons.push(name);
    this.newReasonName = '';
  }

  removeStagedReason(index: number) {
    this.newMachineTypeReasons.splice(index, 1);
  }

  async deleteReason(reason: DowntimeReason) {
    if (!this.editingId) return;
    const ok = await this.confirmService.confirm({
      message: `Delete downtime reason "${reason.name}"? This cannot be undone.`,
      confirmText: 'Delete',
      danger: true,
    });
    if (!ok) return;
    const res = await this.machineTypesApi.deleteReason(this.editingId, reason._id);
    if (!notifyResult(this.toastr, res, `${reason.name} removed.`)) return;
    await this.load();
  }
}
